// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/hooks/useInventory.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, onSnapshot, addDoc, doc, deleteDoc, updateDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { calculateProductStatus } from '../utils/productCalculations';

export function useInventory(userId) {
    // 1. Todos os hooks são chamados no topo, incondicionalmente.
    const [inventory, setInventory] = useState([]);

    useEffect(() => {
        // 2. A "guarda" agora fica DENTRO do useEffect.
        if (!userId) {
            setInventory([]); // Limpa o inventário se o utilizador fizer logout.
            return; // Interrompe a execução do efeito.
        }

        const collectionRef = collection(db, 'users', userId, 'products');
        const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
            const inventoryData = snapshot.docs.map(docSnapshot => {
                const data = docSnapshot.data();
                return {
                    id: docSnapshot.id,
                    ...data,
                    purchaseDate: data.purchaseDate?.toDate ? data.purchaseDate.toDate() : new Date(data.purchaseDate),
                    expiry: data.expiry?.toDate ? data.expiry.toDate() : new Date(data.expiry),
                };
            });
            setInventory(inventoryData);
        });
        return () => unsubscribe();
    }, [userId]);

    const inventoryMetrics = useMemo(() => {
        if (!inventory || inventory.length === 0) {
            return {
                totalValue: 0,
                projectedRevenue: 0,
                potentialProfit: 0,
                totalItems: 0,
                lowStockCount: 0,
                expiringSoonCount: 0,
                categoryChartData: { labels: [], datasets: [] },
                topValueProducts: [],
                monthlyAcquisition: {},
                availableYears: [],
                brandValues: []
            };
        }

        const withStatus = inventory.map(p => ({
            ...p,
            balance: p.initialQty - p.soldQty,
            status: calculateProductStatus(p),
        }));

        const totalValue = withStatus.reduce((acc, p) => acc + (p.balance * p.cost), 0);
        const projectedRevenue = withStatus.reduce((acc, p) => acc + (p.balance > 0 ? p.balance * p.salePrice : 0), 0);
        const potentialProfit = projectedRevenue - totalValue;
        const totalItems = withStatus.reduce((acc, p) => acc + p.balance, 0);
        const lowStockCount = withStatus.filter(p => p.status === 'ESTOQUE BAIXO').length;
        const expiringSoonCount = withStatus.filter(p => p.status === 'VALIDADE PRÓXIMA').length;
        
        const brandMap = new Map();
        withStatus.forEach(p => {
            if (p.balance > 0) {
                const currentTotal = brandMap.get(p.brand) || 0;
                brandMap.set(p.brand, currentTotal + (p.balance * p.cost));
            }
        });
        const brandValues = [...brandMap.entries()].sort((a, b) => b[1] - a[1]);

        const categoryMap = new Map();
        withStatus.forEach(p => {
             if (p.balance > 0) {
                const currentTotal = categoryMap.get(p.category) || 0;
                categoryMap.set(p.category, currentTotal + (p.balance * p.cost));
             }
        });
        const categoryChartData = {
            labels: [...categoryMap.keys()],
            datasets: [{
                data: [...categoryMap.values()],
                backgroundColor: ['#D7BFBD', '#F2DFDB', '#9B9A98', '#e2d6d5', '#f8ebe8', '#C3A9A7'],
                borderColor: '#FFF',
                borderWidth: 2,
            }]
        };

        const topValueProducts = withStatus
            .map(p => ({ name: p.name, stockValue: p.balance * p.cost }))
            .filter(p => p.stockValue > 0)
            .sort((a, b) => b.stockValue - a.stockValue)
            .slice(0, 10);

        const monthlyAcquisition = {};
        inventory.forEach(p => {
            if(!p.purchaseDate) return;
            const purchaseDate = new Date(p.purchaseDate);
            const year = purchaseDate.getFullYear();
            const month = purchaseDate.getMonth();
            if (!monthlyAcquisition[year]) {
                monthlyAcquisition[year] = Array(12).fill(0);
            }
            monthlyAcquisition[year][month] += p.initialQty * p.cost;
        });

        return {
            totalValue,
            projectedRevenue,
            potentialProfit,
            totalItems,
            lowStockCount,
            expiringSoonCount,
            categoryChartData,
            topValueProducts,
            monthlyAcquisition,
            availableYears: Object.keys(monthlyAcquisition).sort((a,b) => b-a),
            brandValues
        };
    }, [inventory]);

    const addProduct = useCallback(async (newProduct) => {
        if (!userId) return;
        try {
            await addDoc(collection(db, 'users', userId, 'products'), newProduct);
        } catch (error) {
            console.error("Erro ao adicionar produto:", error);
            throw error;
        }
    }, [userId]);

    const deleteProduct = useCallback(async (productId) => {
        if (!userId) return;
        try {
            await deleteDoc(doc(db, 'users', userId, 'products', productId));
        } catch (error) {
            console.error("Erro ao deletar produto:", error);
            throw error;
        }
    }, [userId]);

    const deleteMultipleProducts = useCallback(async (productIds) => {
        if (!userId || !productIds || productIds.length === 0) return;
        const batch = writeBatch(db);
        productIds.forEach(id => {
            const docRef = doc(db, 'users', userId, 'products', id);
            batch.delete(docRef);
        });
        try {
            await batch.commit();
        } catch (error) {
            console.error("Erro ao deletar produtos em lote:", error);
            throw error;
        }
    }, [userId]);

    const updateProductPrice = useCallback(async (productId, newPrice) => {
        if (!userId) return;
        try {
            await updateDoc(doc(db, 'users', userId, 'products', productId), { salePrice: newPrice });
        } catch (error) {
            console.error("Erro ao atualizar preço do produto:", error);
            throw error;
        }
    }, [userId]);

    // INSERÇÃO NOVA: Função para editar os dados completos do produto
    const updateProduct = useCallback(async (productId, updatedData) => {
        if (!userId) return;
        try {
            const docRef = doc(db, 'users', userId, 'products', productId);
            await updateDoc(docRef, {
                ...updatedData,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Erro ao atualizar produto:", error);
            throw error;
        }
    }, [userId]);

    const bulkAddProducts = useCallback(async (productsData) => {
        if (!userId || !Array.isArray(productsData) || productsData.length === 0) return;
        
        const batch = writeBatch(db);
        const collectionRef = collection(db, 'users', userId, 'products');

        productsData.forEach((product) => {
            // Usamos o ID aleatório gerado pelo modal para permitir lotes/duplicados
            const docRef = doc(collectionRef, product.id); 
            batch.set(docRef, {
                ...product,
                purchaseDate: product.purchaseDate || serverTimestamp(),
                expiry: product.expiry || null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        });

        try {
            await batch.commit();
        } catch (error) {
            console.error("Erro na importação em lote:", error);
            throw error;
        }
    }, [userId]);

    return { 
        inventory, 
        inventoryMetrics,
        addProduct, 
        deleteProduct, 
        updateProductPrice,
        updateProduct, // <- Exportada aqui para uso nos modais
        deleteMultipleProducts,
        bulkAddProducts
    };
}